<template>
  <!-- Conteneur principal du sous-en-tête de l'application -->
  <div id="app-sub-header-container">
    <!-- Composant de mise en page qui organise le contenu en deux zones -->
    <SubHeaderLayout>
      <!-- Slot pour les liens directs (API, SPARQL, FHIR) -->
      <!-- Le composant DirectLinks reçoit sa configuration via injection de dépendance -->
      <template #linkContent>
        <DirectLinks />
      </template>

      <!-- Slot pour le contenu principal (barre de recherche et sélecteur de langue) -->
      <template #mainContent>
        <SimpleSearchBar :selectedLanguage="selectedLanguage" :languages="availableLanguages" :searchQuery="searchQuery"
          :selectedType="selectedType" :types="availableTypes" :extended-search.sync="extendedSearch"
          @languages-changed="handleLanguagesChange" @types-changed="handleTypesChange"
          @search-submitted="handleSearchSubmission" />
      </template>
    </SubHeaderLayout>
  </div>
</template>

<script>
// Importation des composants enfants
import SubHeaderLayout from './components/SubHeaderLayout.vue';
import SimpleSearchBar from './components/SimpleSearchBar.vue';
import DirectLinks from './components/DirectLinks.vue';
// Importation de la configuration des langues et types par défaut
import { defaultLanguages } from './enums/languages.js';
import { defaultTypes, getAvailableTypeCodes } from './enums/types.js';

export default {
  name: 'AppSubHeader',

  // Enregistrement des composants utilisés dans le template
  components: {
    SubHeaderLayout,
    SimpleSearchBar,
    DirectLinks
  },

  // Injection de dépendance : met à disposition la configuration des liens directs
  // pour tous les composants enfants qui en ont besoin
  provide() {
    return {
      directLinks: this.directLinks
    };
  },

  // Données réactives du composant
  data() {
    return {
      // Langues actuellement sélectionnées (par défaut : français)
      selectedLanguage: 'fr',
      // Liste des langues disponibles dans le sélecteur
      availableLanguages: defaultLanguages,
      // Liste des types disponibles dans le sélecteur
      availableTypes: defaultTypes,
      // Configuration des liens directs affichés dans l'en-tête
      // Chaque lien contient un libellé et une URL de destination
      directLinks: [
        { label: 'API', href: '/api-docs', id: 'direct-link-api' },
        { label: 'SPARQL', href: '/sparql', id: 'direct-link-sparql' },
        { label: 'FHIR', href: '/fhir', id: 'direct-link-fhir' }
      ],
      // Données du formulaire de recherche
      searchQuery: '',
      selectedType: 'all',
      extendedSearch: false
    }
  },

  // Hook de cycle de vie : appelé après la création de l'instance
  created() {
    // Initialisation de la configuration des langues
    this.loadLanguageConfiguration();

    // Initialise les données à partir des paramètres URL
    this.initializeFromUrl();

    this.updateUrlParameters();

  },

  // Propriétés calculées (actuellement vide, prêt pour de futures extensions)
  computed: {
  },

  // Watchers pour mettre à jour l'URL quand les valeurs changent
  watch: {
    // Surveille les changements des paramètres de recherche pour mettre à jour l'URL
    searchQuery() {
      this.updateUrlParameters();
    },

    extendedSearch() {
      this.updateUrlParameters();
    },

    selectedType() {
      this.updateUrlParameters();
    },

    selectedLanguage() {
      this.updateUrlParameters();
    }
  },

  // Méthodes du composant
  methods: {
    /**
     * Gestionnaire d'événement : appelé quand l'utilisateur change de langues
     * @param {string} languagesCode - Code des langues sélectionnées (ex: 'fr', 'en')
     */
    handleLanguagesChange(languagesCode) {
      this.selectedLanguages = languagesCode;
      // Met à jour l'URL avec les nouvelles langues
      this.updateUrlParameters();
    },

    /**
     * Gestionnaire d'événement : appelé quand l'utilisateur change de types
     * @param {string} typesCode - Code des types sélectionnés (ex: 'all', 'termino', 'concept')
     */
    handleTypesChange(typesCode) {
      this.selectedType = typesCode;
      // Met à jour l'URL avec les nouveaux types
      this.updateUrlParameters();
    },

    /**
     * Gestionnaire pour la soumission de recherche
     * @param {Object} searchData - Données de recherche soumises
     */
    handleSearchSubmission(searchData) {
      // Construit les paramètres URL pour la page de recherche
      const searchParams = new URLSearchParams();

      // Ajoute la requête de recherche (obligatoire)
      if (searchData.query && searchData.query.trim()) {
        searchParams.set('search', searchData.query.trim());
      }


      const language = searchData.language;
      if (typeof language === 'string' && language !== 'undefined') {
        searchParams.append('lang', language);
      }

      // Ajoute les types séparément si différent de "all"
      const effectiveSearchTypes = searchData.types || 'all';
      if (effectiveSearchTypes !== 'all') {
        // Si types est une chaîne séparée par des virgules, les diviser
        const typesArray = Array.isArray(effectiveSearchTypes)
          ? effectiveSearchTypes
          : effectiveSearchTypes.split(',').map(type => type.trim());

        // Ajouter chaque type comme un paramètre séparé
        typesArray.forEach(type => {
          if (type && type !== 'all') {
            searchParams.append('type', type);
          }
        });
      }

      // Ajoute les options si activées
      if (searchData.options.extendedSearch) {
        searchParams.set('extendedSearch', 'true');
      }

      // Construit l'URL de recherche
      const searchUrl = `/search?${searchParams.toString()}`;

      // Redirige vers la page de recherche
      window.location.href = searchUrl;
    },

    /**
     * Récupère les paramètres de recherche depuis l'URL
     * @returns {Object} Objet contenant les paramètres parsés
     */
    getUrlParameters() {
      const urlParams = new URLSearchParams(window.location.search);

      // Récupère le paramètre de langue
      const langParam = urlParams.get('lang');
      let selectedLang;

      if (langParam === null) {
        // Paramètre absent : sélectionner 'fr' par défaut
        selectedLang = 'fr';
      } else if (langParam === '') {
        // Paramètre vide : sélectionner 'all'
        selectedLang = 'all';
      } else {
        // Paramètre avec valeur : utiliser cette valeur (fr, en, etc.)
        selectedLang = langParam;
      }

      // Récupère tous les paramètres de type (peut être multiple)
      const allTypes = urlParams.getAll('type');

      return {
        searchQuery: urlParams.get('search') || '',
        selectedLanguages: selectedLang,
        selectedTypes: allTypes.length > 0 ? allTypes : ['all'],
        extendedSearch: urlParams.get('extendedSearch') === 'true'
      };
    },

    /**
     * Met à jour les paramètres de l'URL avec les valeurs actuelles
     */
    updateUrlParameters() {
      // Ne met à jour l'URL que si on est sur la page de recherche
      if (window.location.pathname !== '/search') {
        return;
      }

      const params = new URLSearchParams();

      // Ajoute les paramètres seulement s'ils ont des valeurs non par défaut
      if (this.searchQuery.trim()) {
        params.set('search', this.searchQuery.trim());
      }

      // Ajoute les langues séparément
      if (this.selectedLanguage == 'all') {
        params.append('lang', '');
      } else {
        params.append('lang', this.selectedLanguage);
      }

      if (this.selectedType == 'all') {
        // Ajouter chaque type comme un paramètre séparé
        getAvailableTypeCodes().forEach(type => {
          if (type !== 'all') {
            params.append('type', type);
          } else if (type) {
            params.append('type', type);
          }
        });
      } else {
        params.append('type', this.selectedType);
      }

      if (this.extendedSearch) {
        params.set('extendedSearch', 'true');
      }

      // Met à jour l'URL sans recharger la page
      const newUrl = params.toString() ?
        `${window.location.pathname}?${params.toString()}` :
        window.location.pathname;

      window.history.replaceState({}, '', newUrl);
    },

    /**
     * Initialise les données à partir des paramètres URL
     */
    initializeFromUrl() {
      const urlParams = this.getUrlParameters();
      // Met à jour les données du composant avec les valeurs de l'URL
      this.searchQuery = urlParams.searchQuery;
      this.extendedSearch = urlParams.extendedSearch;

      // Récupère la langue sélectionnée depuis l'URL
      this.selectedLanguage = urlParams.selectedLanguages;

      // si selectedTypes est un tableau , prendre le premier élément
      if (Array.isArray(urlParams.selectedTypes)) {
        this.selectedType = urlParams.selectedTypes.length > 1 ? 'all' : urlParams.selectedTypes[0];
      }
    },

    /**
     * Charge la configuration des langues disponibles
     * Cette méthode peut être étendue pour récupérer les langues depuis une API
     */
    loadLanguageConfiguration() {
      this.availableLanguages = defaultLanguages;
    }
  },
}
</script>

<style scoped>
/* Styles spécifiques au composant AppSubHeader */

/* Conteneur principal : ajoute un espacement horizontal pour éviter que le contenu
   touche les bords de l'écran */
#app-sub-header-container {
  padding-left: 1rem;
  padding-right: 1rem;
}
</style>
